import { cn } from '@veraclins-dev/utils';

import {
  spinnerTrackVariants,
  type SpinnerVariants,
  spinnerVariants,
} from './utils/variants/spinner';
import { extractStyleProps } from './utils/variants/styles';

// Derived from the cva (single source of truth): `spinnerSize` is the diameter
// scale and `tone` the semantic status (incl. `current`, which inherits
// `currentColor` for use inside filled buttons). NonNullable strips the optional
// `| undefined` so the unions can index the stroke-width map. (Regular cva
// variants are plain literal unions; only the responsive style props are
// ResponsiveValue.)
type SpinnerSize = NonNullable<SpinnerVariants['spinnerSize']>;
type SpinnerTone = NonNullable<SpinnerVariants['tone']>;

// Stroke width per size, on the 24×24 viewBox (scaled up so the ring reads at
// the rendered px diameter from the spec: xs 1.5px → md 2.5px → xl 4px).
const strokeWidth: Record<SpinnerSize, number> = {
  xs: 3,
  sm: 3.5,
  md: 3.5,
  lg: 3,
  xl: 2.5,
};

// Style props (margin, padding, width, …) — everything on the cva except the
// two real variants, which are declared explicitly above.
type SpinnerStyleProps = Omit<SpinnerVariants, 'spinnerSize' | 'tone'>;

type SpinnerProps = Omit<React.ComponentProps<'span'>, 'color'> &
  SpinnerStyleProps & {
    spinnerSize?: SpinnerSize;
    tone?: SpinnerTone;
    label?: React.ReactNode;
    srLabel?: string;
    trackVisible?: boolean;
  };

const RADIUS = 10;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
// Arc covers ~72% of the circle (a 28% gap), lengthening to ~60% coverage is
// handled by Tailwind under reduced motion — see the dash classes below.
const ARC = CIRCUMFERENCE * 0.72;

/**
 * Indeterminate loading indicator — a continuously rotating SVG ring for inline
 * and full-region waits. Single element (no compound parts). Complements
 * `Skeleton` (layout placeholder) and `ProgressBar` (determinate progress).
 *
 * `role="status"` + `aria-live="polite"` so assistive tech announces the wait;
 * the accessible name comes from a visible `label` (rendered inline-end of the
 * ring) or the `srLabel` fallback. The SVG itself is `aria-hidden`.
 * @category data-content
 */
function Spinner({
  className,
  spinnerSize = 'md',
  tone = 'neutral',
  label,
  srLabel = 'Loading',
  trackVisible = true,
  ...props
}: SpinnerProps) {
  const { styleProps, others } = extractStyleProps(props);
  // `role` is held in a variable (not a literal) so the spinner reads as a live
  // status region without oxlint's prefer-tag-over-role nudging it toward
  // `<output>` (a form-output element, wrong semantics for a loading wait).
  const role = 'status';

  return (
    <span
      data-slot="spinner"
      data-tone={tone}
      role={role}
      aria-live="polite"
      aria-label={label ? undefined : srLabel || undefined}
      className={cn('inline-flex items-center gap-2', className)}
      {...others}
    >
      <svg
        data-slot="spinner-ring"
        aria-hidden="true"
        viewBox="0 0 24 24"
        fill="none"
        className={cn(
          'animate-spin [animation-duration:700ms] [animation-timing-function:linear] motion-reduce:[animation-duration:2s]',
          spinnerVariants({ spinnerSize, tone, ...styleProps }),
        )}
      >
        {trackVisible ? (
          <circle
            cx="12"
            cy="12"
            r={RADIUS}
            strokeWidth={strokeWidth[spinnerSize]}
            className={spinnerTrackVariants({ tone })}
          />
        ) : null}
        <circle
          cx="12"
          cy="12"
          r={RADIUS}
          strokeWidth={strokeWidth[spinnerSize]}
          strokeLinecap="round"
          strokeDasharray={`${ARC} ${CIRCUMFERENCE}`}
          className="motion-reduce:[stroke-dasharray:none]"
        />
      </svg>
      {label ? <span data-slot="spinner-label">{label}</span> : null}
    </span>
  );
}

export { Spinner, type SpinnerProps };

// for storybook
export default Spinner;
